import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  ChartBarIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  CubeIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

import toast from 'react-hot-toast';

const AdminReports = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  const apiGet = useCallback(async (url) => {
    const token = getToken(true);
    if (!token) {
      toast.error('Authentication required. Please login again.');
      navigate('/dashboard/login');
      throw new Error('No token');
    }

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const data = await response.json().catch(() => ({ success: false, message: 'Invalid server response' }));
    if (!response.ok || !data?.success) {
      const message = data?.message || `Request failed (${response.status})`;
      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/dashboard/login');
      } else if (response.status === 403) {
        toast.error(message || 'You do not have permission to view reports.');
      } else {
        toast.error(message);
      }
      throw new Error(message);
    }

    return data;
  }, [getToken, navigate]);
  
  // Date filters
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(1)).toISOString().split('T')[0], // First day of month
    to: new Date().toISOString().split('T')[0] // Today
  });
  
  // Report data
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [salesSummary, setSalesSummary] = useState([]);
  const [orderRegister, setOrderRegister] = useState([]);
  const [purchaseSummary, setPurchaseSummary] = useState([]);
  const [purchaseInvoiceRegister, setPurchaseInvoiceRegister] = useState([]);
  const [stockOnHand, setStockOnHand] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [profitReport, setProfitReport] = useState([]);
  const [referralSummary, setReferralSummary] = useState(null);
  const [referralUsages, setReferralUsages] = useState([]);
  
  // Filters
  const [salesGroupBy, setSalesGroupBy] = useState('date');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [profitGroupBy, setProfitGroupBy] = useState('date');
  const [referralStatusFilter, setReferralStatusFilter] = useState('');
  const [referrerFilter, setReferrerFilter] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);

  const fetchSuppliers = useCallback(async () => {
    try {
      const data = await apiGet('/api/purchases/suppliers');
      if (data.success) {
        setSuppliers(data.data || []);
      } else {
        // If suppliers endpoint doesn't exist, try alternative
        console.warn('Suppliers endpoint not available');
        setSuppliers([]);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  }, [apiGet]);

  const fetchDashboardSummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/api/reports/dashboard-summary?from=${dateRange.from}&to=${dateRange.to}`);
      if (data.success) {
        setDashboardSummary(data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet, dateRange.from, dateRange.to]);

  const fetchSalesSummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/api/reports/sales-summary?from=${dateRange.from}&to=${dateRange.to}&groupBy=${salesGroupBy}`);
      if (data.success) {
        setSalesSummary(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching sales summary:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet, dateRange.from, dateRange.to, salesGroupBy]);

  const fetchOrderRegister = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/order-register?from=${dateRange.from}&to=${dateRange.to}`;
      if (orderStatusFilter) url += `&status=${orderStatusFilter}`;

      const data = await apiGet(url);
      if (data.success) {
        setOrderRegister(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching order register:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet, dateRange.from, dateRange.to, orderStatusFilter]);

  const fetchPurchaseSummary = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/purchase-summary?from=${dateRange.from}&to=${dateRange.to}`;
      if (supplierFilter) url += `&supplierId=${supplierFilter}`;

      const data = await apiGet(url);
      if (data.success) {
        setPurchaseSummary(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching purchase summary:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet, dateRange.from, dateRange.to, supplierFilter]);

  const fetchPurchaseInvoiceRegister = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/purchase-invoice-register?from=${dateRange.from}&to=${dateRange.to}`;
      if (supplierFilter) url += `&supplierId=${supplierFilter}`;

      const data = await apiGet(url);
      if (data.success) {
        setPurchaseInvoiceRegister(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching purchase invoice register:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet, dateRange.from, dateRange.to, supplierFilter]);

  const fetchStockOnHand = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/reports/stock-on-hand');
      if (data.success) {
        setStockOnHand(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching stock on hand:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet]);

  const fetchLowStock = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/reports/low-stock');
      if (data.success) {
        setLowStock(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching low stock:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet]);

  const fetchProfitReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/api/reports/profit?from=${dateRange.from}&to=${dateRange.to}&groupBy=${profitGroupBy}`);
      if (data.success) {
        setProfitReport(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching profit report:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet, dateRange.from, dateRange.to, profitGroupBy]);

  const fetchReferralReport = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/reports/referral-usage?from=${dateRange.from}&to=${dateRange.to}`;
      if (referralStatusFilter) url += `&status=${referralStatusFilter}`;
      if (referrerFilter) url += `&referrer=${encodeURIComponent(referrerFilter)}`;

      const data = await apiGet(url);
      if (data.success) {
        setReferralSummary(data.data?.summary || null);
        setReferralUsages(data.data?.usages || []);
      }
    } catch (error) {
      console.error('Error fetching referral report:', error);
    } finally {
      setLoading(false);
    }
  }, [apiGet, dateRange.from, dateRange.to, referralStatusFilter, referrerFilter]);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'employee')) {
      fetchSuppliers();
      fetchDashboardSummary();
    } else {
      navigate('/');
    }
  }, [fetchDashboardSummary, fetchSuppliers, navigate, user]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardSummary();
    } else if (activeTab === 'sales') {
      fetchSalesSummary();
      fetchOrderRegister();
    } else if (activeTab === 'purchase') {
      fetchPurchaseSummary();
      fetchPurchaseInvoiceRegister();
    } else if (activeTab === 'inventory') {
      fetchStockOnHand();
      fetchLowStock();
    } else if (activeTab === 'profit') {
      fetchProfitReport();
    } else if (activeTab === 'referrals') {
      fetchReferralReport();
    }
  }, [activeTab, fetchDashboardSummary, fetchLowStock, fetchOrderRegister, fetchProfitReport, fetchPurchaseInvoiceRegister, fetchPurchaseSummary, fetchReferralReport, fetchSalesSummary, fetchStockOnHand]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Report tables are wider than a phone screen, so they scroll horizontally inside
  // their own container instead of being clipped - this hint makes that discoverable
  // on mobile instead of looking like the data is just cut off.
  const ScrollHint = () => (
    <p className="sm:hidden text-xs text-gray-400 mt-2 flex items-center gap-1">
      <span aria-hidden="true">↔</span> Swipe left/right to see more columns
    </p>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportToExcel = (data, filename, sheetName = 'Report') => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      // Convert data to worksheet format
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      
      // Generate Excel file
      XLSX.writeFile(workbook, `${filename}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export to Excel. Please try again.');
    }
  };

  const exportToPDF = (data, filename, title = 'Report', columns = null) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text(title, 14, 15);
      
      // Add date range if available
      doc.setFontSize(10);
      doc.text(`Period: ${dateRange.from} to ${dateRange.to}`, 14, 22);
      
      // Prepare table data
      const tableData = Array.isArray(data) ? data : [data];
      
      // Auto-detect columns if not provided
      let tableColumns = columns;
      if (!tableColumns && tableData.length > 0) {
        tableColumns = Object.keys(tableData[0]).map(key => ({
          header: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          dataKey: key
        }));
      }

      // Convert data to array format for autotable
      const rows = tableData.map(row => 
        tableColumns.map(col => {
          const value = row[col.dataKey];
          if (value === null || value === undefined) return '';
          if (typeof value === 'object' && value !== null) {
            // Handle nested objects
            if (Array.isArray(value)) return value.join(', ');
            return JSON.stringify(value);
          }
          // Format numbers with 2 decimal places if they look like currency
          if (typeof value === 'number' && (col.dataKey.toLowerCase().includes('price') || 
              col.dataKey.toLowerCase().includes('amount') || 
              col.dataKey.toLowerCase().includes('total') ||
              col.dataKey.toLowerCase().includes('sales'))) {
            return value.toFixed(2);
          }
          return value.toString();
        })
      );

      const headers = tableColumns.map(col => col.header);

      // Add table
      doc.autoTable({
        head: [headers],
        body: rows,
        startY: 30,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 30 }
      });

      // Add footer with page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      doc.save(`${filename}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export to PDF. Please try again.');
    }
  };

  const getCurrentReportData = () => {
    switch (activeTab) {
      case 'dashboard':
        // For dashboard, create a summary table
        if (!dashboardSummary) return null;
        const summaryData = {
          'Net Sales': dashboardSummary.totals?.net_sales || 0,
          'Gross Sales': dashboardSummary.totals?.gross_sales || 0,
          'Total Transactions': dashboardSummary.totals?.total_transactions || 0,
          'Average Bill Value': dashboardSummary.totals?.average_bill_value || 0,
          'Total Quantity Sold': dashboardSummary.totals?.total_quantity_sold || 0,
          'Total Discounts': dashboardSummary.totals?.total_discounts || 0,
          'Total Tax': dashboardSummary.totals?.total_tax || 0,
          'Total Orders': dashboardSummary.orders?.total || 0,
          'Completed Orders': dashboardSummary.orders?.completed || 0,
          'Pending Orders': dashboardSummary.orders?.pending || 0,
          'Cancelled Orders': dashboardSummary.orders?.cancelled || 0,
          'Total Customers': dashboardSummary.customers?.total || 0,
          'New Customers': dashboardSummary.customers?.new || 0,
          'Today Sales': dashboardSummary.sales?.today || 0,
          'Weekly Sales': dashboardSummary.sales?.weekly || 0,
          'Monthly Sales': dashboardSummary.sales?.monthly || 0,
          'Profit': dashboardSummary.profit?.profit || 0,
          'Returns Count': dashboardSummary.returns?.count || 0,
          'Refund Amount': dashboardSummary.returns?.amount || 0
        };
        return [summaryData];
      case 'sales':
        return Array.isArray(orderRegister) ? orderRegister : [];
      case 'purchase':
        return Array.isArray(purchaseInvoiceRegister) ? purchaseInvoiceRegister : [];
      case 'inventory':
        return Array.isArray(stockOnHand) ? stockOnHand : [];
      case 'profit':
        return Array.isArray(profitReport) ? profitReport : [];
      case 'referrals':
        return Array.isArray(referralUsages) ? referralUsages : [];
      default:
        return [];
    }
  };

  const getCurrentReportTitle = () => {
    switch (activeTab) {
      case 'dashboard':
        return 'Dashboard Summary Report';
      case 'sales':
        return 'Sales Report';
      case 'purchase':
        return 'Purchase Report';
      case 'inventory':
        return 'Inventory Report';
      case 'profit':
        return 'Profit & Loss Report';
      case 'referrals':
        return 'Referral Rewards Report';
      default:
        return 'Report';
    }
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard Summary', icon: ChartBarIcon },
    { id: 'sales', name: 'Sales Reports', icon: CurrencyDollarIcon },
    { id: 'purchase', name: 'Purchase Reports', icon: ShoppingBagIcon },
    { id: 'inventory', name: 'Inventory Reports', icon: CubeIcon },
    { id: 'profit', name: 'Profit & Loss', icon: ArrowTrendingUpIcon },
    { id: 'referrals', name: 'Referral Rewards', icon: UserGroupIcon }
  ];

  if (loading && !dashboardSummary) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between min-w-0">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Reports & Analytics</h1>
            <p className="mt-2 text-gray-600">Comprehensive business intelligence and reporting</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row w-full min-w-0 sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex w-full items-center justify-center rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 sm:w-auto"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Export
              </button>
              {showExportMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowExportMenu(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          const data = getCurrentReportData();
                          if (data) {
                            exportToCSV(Array.isArray(data) ? data : [data], `${activeTab}-report-${Date.now()}`);
                            setShowExportMenu(false);
                          }
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Export as CSV
                      </button>
                      <button
                        onClick={() => {
                          const data = getCurrentReportData();
                          if (data) {
                            exportToExcel(Array.isArray(data) ? data : [data], `${activeTab}-report-${Date.now()}`, getCurrentReportTitle());
                            setShowExportMenu(false);
                          }
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Export as Excel
                      </button>
                      <button
                        onClick={() => {
                          const data = getCurrentReportData();
                          if (data) {
                            exportToPDF(Array.isArray(data) ? data : [data], `${activeTab}-report-${Date.now()}`, getCurrentReportTitle());
                            setShowExportMenu(false);
                          }
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Export as PDF
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => window.print()}
              className="flex w-full items-center justify-center rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700 sm:w-auto"
            >
              <PrinterIcon className="h-5 w-5 mr-2" />
              Print
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-6 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Dashboard Summary Tab */}
        {activeTab === 'dashboard' && dashboardSummary && (
          <div className="space-y-6">
            {/* Quick Sales KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Total Sales (Net)</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(dashboardSummary.totals?.net_sales)}
                </p>
                <p className="mt-1 text-xs text-gray-500">After discount</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Gross Sales</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(dashboardSummary.totals?.gross_sales)}
                </p>
                <p className="mt-1 text-xs text-gray-500">Before discount</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardSummary.totals?.total_transactions || 0}
                </p>
                <p className="mt-1 text-xs text-gray-500">Bills / Invoices</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Average Bill Value</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(dashboardSummary.totals?.average_bill_value)}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Total Sales ÷ Total Transactions
                </p>
              </div>
            </div>

            {/* Period Sales Snapshot (Today / Week / Month) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Today Sales</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(dashboardSummary.sales?.today)}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Weekly Sales</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(dashboardSummary.sales?.weekly)}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Monthly Sales</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(dashboardSummary.sales?.monthly)}
                </p>
              </div>
            </div>

            {/* Orders & Customers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Orders Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Orders</span>
                    <span className="font-semibold">{dashboardSummary.orders?.total || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completed</span>
                    <span className="text-green-600 font-semibold">{dashboardSummary.orders?.completed || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending</span>
                    <span className="text-yellow-600 font-semibold">{dashboardSummary.orders?.pending || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cancelled</span>
                    <span className="text-red-600 font-semibold">{dashboardSummary.orders?.cancelled || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customers Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Customers</span>
                    <span className="font-semibold">{dashboardSummary.customers?.total || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">New Customers</span>
                    <span className="text-blue-600 font-semibold">{dashboardSummary.customers?.new || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue & Profit */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Net Sales</span>
                    <span className="font-semibold">
                      {formatCurrency(dashboardSummary.totals?.net_sales)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Refunds</span>
                    <span className="text-red-600 font-semibold">
                      {formatCurrency(dashboardSummary.returns?.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-semibold">Net Revenue</span>
                    <span className="font-bold text-gray-900">
                      {formatCurrency(
                        (dashboardSummary.totals?.net_sales || 0) -
                        (dashboardSummary.returns?.amount || 0)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Quantity Sold</span>
                    <span className="font-semibold">
                      {dashboardSummary.totals?.total_quantity_sold || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Discounts</span>
                    <span className="font-semibold">
                      {formatCurrency(dashboardSummary.totals?.total_discounts)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Tax</span>
                    <span className="font-semibold">
                      {formatCurrency(dashboardSummary.totals?.total_tax)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Profit</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sales</span>
                    <span>{formatCurrency(dashboardSummary.profit?.sales)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost</span>
                    <span>{formatCurrency(dashboardSummary.profit?.cost)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-semibold">Net Profit</span>
                    <span className={`font-bold ${dashboardSummary.profit?.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(dashboardSummary.profit?.profit)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Products */}
            {dashboardSummary.topProducts && dashboardSummary.topProducts.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Selling Products</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[720px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity Sold</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dashboardSummary.topProducts.map((product) => (
                        <tr key={product.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.sku}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.total_sold || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(product.total_revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ScrollHint />
              </div>
            )}

            {/* Low Stock Alerts */}
            {dashboardSummary.lowStock && dashboardSummary.lowStock.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Low Stock Alerts</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[640px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Stock</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Stock</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dashboardSummary.lowStock.map((product) => (
                        <tr key={product.id} className={product.stock_quantity === 0 ? 'bg-red-50' : 'bg-yellow-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.stock_quantity}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.min_stock_qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ScrollHint />
              </div>
            )}

            {/* Tender / Payment Breakdown & Returns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tender / Payment Report */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tender / Payment Report</h3>
                {dashboardSummary.tender && dashboardSummary.tender.length > 0 ? (
                  <>
                  <div className="overflow-x-auto">
                    <table className="min-w-[640px] w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transactions</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {dashboardSummary.tender.map((tender, idx) => (
                          <tr key={idx}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {tender.payment_mode || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {tender.transaction_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(tender.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ScrollHint />
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">No transactions in this period.</p>
                )}
              </div>

              {/* Returns & Refunds */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Returns & Refunds</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Refund Count</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardSummary.returns?.count || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Refund Amount</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(dashboardSummary.returns?.amount)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sales Reports Tab */}
        {activeTab === 'sales' && (
          <div className="space-y-6">
            {/* Sales Summary Filter */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <label className="text-sm font-medium text-gray-700">Group By:</label>
                <select
                  value={salesGroupBy}
                  onChange={(e) => setSalesGroupBy(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 lg:w-auto"
                >
                  <option value="date">Date</option>
                  <option value="category">Category</option>
                  <option value="product">Product</option>
                  <option value="customer">Customer</option>
                  <option value="payment">Payment Mode</option>
                </select>
                <label className="text-sm font-medium text-gray-700 lg:ml-4">Order Status:</label>
                <select
                  value={orderStatusFilter}
                  onChange={(e) => setOrderStatusFilter(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 lg:w-auto"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="packed">Packed</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Sales Summary Table */}
            {salesSummary.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales Summary</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[840px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {salesGroupBy === 'date' && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                          </>
                        )}
                        {salesGroupBy === 'category' && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                          </>
                        )}
                        {salesGroupBy === 'product' && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                          </>
                        )}
                        {salesGroupBy === 'customer' && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                          </>
                        )}
                        {salesGroupBy === 'payment' && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {salesSummary.map((row, idx) => (
                        <tr key={idx}>
                          {salesGroupBy === 'date' && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(row.date)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.order_count}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.item_count}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.total_sales)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.total_tax)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.total_discount)}</td>
                            </>
                          )}
                          {salesGroupBy === 'category' && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.category}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.order_count}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.total_quantity}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.total_sales)}</td>
                            </>
                          )}
                          {salesGroupBy === 'product' && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.sku}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.total_quantity}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.total_sales)}</td>
                            </>
                          )}
                          {salesGroupBy === 'customer' && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.customer_name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.email}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.order_count}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.total_sales)}</td>
                            </>
                          )}
                          {salesGroupBy === 'payment' && (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.payment_mode}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.order_count}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.total_sales)}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {salesSummary.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <p className="text-sm text-gray-600">No sales summary data for the selected date range.</p>
              </div>
            )}

            {/* Order Register */}
            {orderRegister.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Register</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[980px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Value</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orderRegister.map((order) => (
                        <tr key={order.order_id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.order_number}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(order.order_date)} {order.order_time}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.customer_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.items_count}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.payment_method}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              order.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                              order.order_status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {order.order_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(order.subtotal)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{formatCurrency(order.grand_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {orderRegister.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <p className="text-sm text-gray-600">No orders found for the selected date range/status.</p>
              </div>
            )}
          </div>
        )}

        {/* Purchase Reports Tab */}
        {activeTab === 'purchase' && (
          <div className="space-y-6">
            {/* Supplier Filter */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <label className="text-sm font-medium text-gray-700">Supplier:</label>
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 lg:w-auto"
                >
                  <option value="">All Suppliers</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Purchase Summary */}
            {purchaseSummary.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Purchase Summary by Supplier</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[760px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Invoices</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Purchase</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {purchaseSummary.map((summary) => (
                        <tr key={summary.supplier_id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{summary.supplier_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{summary.total_invoices}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(summary.total_purchase)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(summary.total_paid)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">{formatCurrency(summary.total_due)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Purchase Invoice Register */}
            {purchaseInvoiceRegister.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Purchase Invoice Register</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[1120px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice No</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier Invoice</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grand Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {purchaseInvoiceRegister.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.purchase_invoice_no}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.supplier_invoice_no || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.supplier_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(invoice.invoice_date)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(invoice.subtotal)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(invoice.tax_total)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{formatCurrency(invoice.grand_total)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(invoice.paid_amount)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">{formatCurrency(invoice.balance_due)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              invoice.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                              invoice.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {invoice.payment_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referral Status</label>
                  <select
                    value={referralStatusFilter}
                    onChange={(e) => setReferralStatusFilter(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  >
                    <option value="">All Statuses</option>
                    <option value="APPLIED">Applied</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                    <option value="REVERSED">Reversed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referrer Search</label>
                  <input
                    type="text"
                    value={referrerFilter}
                    onChange={(e) => setReferrerFilter(e.target.value)}
                    placeholder="Name, email or referral code"
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={fetchReferralReport}
                    className="w-full rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Total Usages</p>
                <p className="text-3xl font-bold text-gray-900">{referralSummary?.total_usages || 0}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Completed</p>
                <p className="text-3xl font-bold text-green-600">{referralSummary?.completed_count || 0}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Applied</p>
                <p className="text-3xl font-bold text-blue-600">{referralSummary?.applied_count || 0}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Cancelled / Reversed</p>
                <p className="text-3xl font-bold text-red-600">{(referralSummary?.cancelled_count || 0) + (referralSummary?.reversed_count || 0)}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Reward Value</p>
                <p className="text-3xl font-bold text-gray-900">{Number(referralSummary?.total_reward_value || 0).toFixed(2)}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Referral Usage Register</h3>
              {referralUsages.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-[1280px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied At</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referral Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Used By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reward</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reward Target</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {referralUsages.map((usage) => (
                        <tr key={usage.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(usage.created_at)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{usage.referral_code}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div>{usage.used_by_name?.trim() || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{usage.used_by_email || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div>{usage.referrer_name?.trim() || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{usage.referrer_email || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{usage.order_number || usage.order_id || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{usage.order_status || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{usage.payment_status || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {usage.reward_type ? `${usage.reward_type} (${Number(usage.reward_value || 0).toFixed(2)})` : 'Pending'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{usage.reward_target || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              usage.status === 'COMPLETED'
                                ? 'bg-green-100 text-green-800'
                                : usage.status === 'APPLIED'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-red-100 text-red-800'
                            }`}>
                              {usage.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-600">No referral usage records found for the selected filters.</p>
              )}
            </div>
          </div>
        )}

        {/* Inventory Reports Tab */}
        {activeTab === 'inventory' && (
          <div className="space-y-6">
            {/* Stock On Hand */}
            {stockOnHand.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Stock On Hand</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[980px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Qty</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Stock</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Value</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stockOnHand.map((product) => (
                        <tr key={product.id} className={
                          product.status === 'Out of Stock' ? 'bg-red-50' :
                          product.status === 'Low Stock' ? 'bg-yellow-50' : ''
                        }>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.sku}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category_name || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.stock_quantity}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.min_stock_qty}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(product.cost_price)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(product.stock_value)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              product.status === 'Out of Stock' ? 'bg-red-100 text-red-800' :
                              product.status === 'Low Stock' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {product.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Low Stock Alerts */}
            {lowStock.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Low Stock Alerts</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[760px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Stock</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Stock</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {lowStock.map((product) => (
                        <tr key={product.id} className="bg-yellow-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.sku}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category_name || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-semibold">{product.stock_quantity}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.min_stock_qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profit & Loss Tab */}
        {activeTab === 'profit' && (
          <div className="space-y-6">
            {/* Profit Group By Filter */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <label className="text-sm font-medium text-gray-700">Group By:</label>
                <select
                  value={profitGroupBy}
                  onChange={(e) => setProfitGroupBy(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 lg:w-auto"
                >
                  <option value="date">Date</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
            </div>

            {/* Profit Report */}
            {profitReport.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Profit & Loss Report</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-[900px] w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purchase Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gross Profit</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discounts</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Profit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {profitReport.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.order_count}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.sales)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.purchase_cost)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">{formatCurrency(row.gross_profit)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.discounts)}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${
                            row.net_profit >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(row.net_profit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
